import {Typography} from 'components/Typography';
import {getMediaSrc} from 'core/helpers/media';
import React, {FC, useEffect, useState} from 'react';
import style from './style.module.scss';
import {Box} from 'components/Grid';
import classNames from 'classnames';
import {MODALS} from 'modules/modals/constants';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {showModal} from 'modules/modals/thunks';
import {useCurrentStockXData} from 'modules/stock-x/hooks/useCurrentStockXData';
import {useSanitizeString} from 'core/hooks/useSanitizeString';
import {format, parseISO} from 'date-fns';
import {ru} from 'date-fns/locale';
import Icon from 'components/Icon';
import DOMPurify from 'isomorphic-dompurify';
import {CollapseAnimated} from 'components/CollapseAnimated';
import parse from 'html-react-parser';
import {CollapseBannerDescription} from 'modules/stock-x/components/Banner/components/CollapseBannerText';
import {useInView} from 'react-intersection-observer';
import {useBannerViewGtag} from 'hooks/analytics/gtag/banners/useBannerViewGtag';
import {useBannerClickGtag} from 'hooks/analytics/gtag/banners/useBannerClickGtag';

// eslint-disable-next-line complexity,max-lines-per-function
export const Banner: FC = () => {
    const {data: stockData} = useCurrentStockXData();

    const dispatch = useAppDispatch();

    const title = useSanitizeString(stockData?.title ?? '');
    const description = useSanitizeString(stockData?.description ?? '');

    const bannerClickGtag = useBannerClickGtag();

    const handleShowTermsClick = () => {
        dispatch(showModal(MODALS.STOCK_X_TERMS_MODAL.name));
        bannerClickGtag({
            type: 'common',
            banner: {
                id: stockData?.id,
                name: stockData?.title ?? '',
                position: 1,
            },
            creative: 'promo_page_main',
        });
    };

    // TODO: будет вынесено бекендом в отдельное поле, которое просто будем брать из ответа
    const formatPeriod = (startIso?: string, endIso?: string): string => {
        if (!startIso || !endIso) {
            return '';
        }
        const start = parseISO(startIso);
        const end = parseISO(endIso);
        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            return '';
        }

        const sameDay = start.toDateString() === end.toDateString();
        const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();

        if (sameDay) {
            return format(start, 'd MMMM', {locale: ru});
        }

        if (sameMonth) {
            return `с ${format(start, 'd', {locale: ru})} по ${format(end, 'd MMMM', {locale: ru})}`;
        }

        return `с ${format(start, 'd MMMM', {locale: ru})} по ${format(end, 'd MMMM', {locale: ru})}`;
    };

    const periodText =
        stockData?.periodText ||
        formatPeriod(stockData?.startDate as string | undefined, stockData?.endDate as string | undefined);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isDetailActionVisible, setIsDetailActionVisible] = useState(false);

    // @ts-expect-error
    // eslint-disable-next-line consistent-return
    useEffect(() => {
        if (stockData?.detailAction && isExpanded) {
            // const timerId = setTimeout(() => {
            setIsDetailActionVisible(true);
            // }, 300);

            return () => {
                // clearTimeout(timerId);
                setIsDetailActionVisible(false);
            };
        }

        setIsDetailActionVisible(false);
    }, [isExpanded, stockData?.detailAction]);

    const labelWithIcon = stockData?.discount?.includes('%');

    const bannerViewGtag = useBannerViewGtag();

    const {ref} = useInView({
        triggerOnce: true,
        threshold: 0.5,
        onChange: (inView) => {
            if (inView && stockData?.id) {
                bannerViewGtag({
                    type: 'stock',
                    creative: 'promo_page_main',
                    banners: [{id: stockData.id, name: title}],
                });
            }
        },
    });

    return (
        <Box className={style.banner} commonPaddingOnMobileTablet={true} ref={ref}>
            <div
                className={classNames(style.main)}
                style={{
                    backgroundColor: stockData?.bgColor ?? 'transparent',
                    // обратная совместимость с b2c (СФ)
                    ...(stockData?.images?.detailImage && !stockData?.images.wideImage
                        ? {
                              backgroundImage: `url(${getMediaSrc(stockData?.images?.detailImage, {width: 9999, isWebp: true})})`,
                          }
                        : {}),
                }}
            >
                <div className={style.content}>
                    <div style={{color: stockData?.textColor ?? '#fff'}}>
                        {title && (
                            <Typography
                                element={'h1'}
                                variants={{
                                    DT: 'ch-display-42-reg',
                                    TB: 'ch-title-32-reg',
                                    MB: 'ch-title-20-reg',
                                }}
                            >
                                {title}
                            </Typography>
                        )}
                        <div className={style.description}>
                            <Typography variant={'body-14-reg'}>{parse(DOMPurify.sanitize(description))}</Typography>
                            {stockData?.detailAction && (
                                <button className={style.fullTerms} onClick={handleShowTermsClick}>
                                    <Typography variant={'label-14-r'}>Полные условия</Typography>
                                </button>
                            )}
                        </div>
                    </div>
                    {(stockData?.discount || periodText) && (
                        <div className={style.bottomArea}>
                            {stockData?.discount && (
                                <div
                                    className={classNames(style.label, {
                                        [style.withIcon]: labelWithIcon,
                                    })}
                                >
                                    {labelWithIcon && <Icon icon={'percent-shield'} isAutoSize={true} />}
                                    <Typography
                                        variants={{
                                            DT: 'label-14-m',
                                            TB: 'label-14-m',
                                            MB: 'label-12-m',
                                        }}
                                        color={'gray100'}
                                    >
                                        {stockData.discount}
                                    </Typography>
                                </div>
                            )}
                            {periodText && (
                                <Typography
                                    className={style.bottomAreaPeriod}
                                    variants={{
                                        DT: 'label-14-r',
                                        TB: 'label-14-r',
                                        MB: 'label-12-r',
                                    }}
                                >
                                    {periodText}
                                </Typography>
                            )}
                        </div>
                    )}
                </div>
                {/* обратная совместимость с b2c (СФ)*/}
                {stockData?.images?.wideImage ? (
                    <img
                        alt={''}
                        loading={'eager'}
                        className={style.imageWide}
                        src={getMediaSrc(stockData.images.wideImage, {
                            width: 9999,

                            isWebp: true,
                        })}
                    />
                ) : (
                    !stockData?.images?.detailImage &&
                    stockData?.images?.promoImage && (
                        <img
                            alt={''}
                            loading={'eager'}
                            className={style.image}
                            src={getMediaSrc(stockData.images.promoImage, {
                                width: 9999,
                                isWebp: true,
                            })}
                        />
                    )
                )}
            </div>
            <div className={style.mobileDescription}>
                {description && (
                    <CollapseAnimated>
                        <Typography variant={'label-14-r'}>
                            <CollapseBannerDescription
                                collapsedButtonText={'...ещё'}
                                text={description}
                                // eslint-disable-next-line no-shadow
                                onChange={(isExpanded) => setIsExpanded(!isExpanded)}
                            />
                        </Typography>
                    </CollapseAnimated>
                )}
                {stockData?.detailAction && (
                    <button
                        className={classNames(style.fullTerms, style.fullTermsAnimated, style.isMobile, {
                            [style.isVisible]: isDetailActionVisible,
                        })}
                        onClick={handleShowTermsClick}
                    >
                        <Typography variant={'label-14-r'}>Полные условия</Typography>
                    </button>
                )}
            </div>
        </Box>
    );
};
